import { useFemaStore } from '../state/femaStore';
import { sanitizePII } from './sanitize';
import { classifyError } from './classifiers';
import { rateLimit } from '../lib/rateLimit';

// Import FemaError interface
interface FemaError {
  id: string;
  timestamp: number;
  category: string;
  severity: 'error' | 'warning' | 'info';
  message: string;
  stack?: string;
  context: Record<string, any>;
  fingerprint: string;
  reportAttempts: number;
  reported: boolean;
}

// Type definitions
interface FemaCaptureOptions {
  category?: string;
  severity?: 'error' | 'warning' | 'info';
  context?: Record<string, any>;
  skipSanitization?: boolean;
  user?: Record<string, any>;
  [key: string]: any;
}

interface ErrorInfo {
  message: string;
  stack: string | null;
  name: string;
  cause?: any;
  [key: string]: any;
}

interface ErrorClassification {
  category: string;
  severity: 'error' | 'warning' | 'info';
  [key: string]: any;
}

interface ReactErrorInfo {
  componentStack?: string;
  [key: string]: any;
}

// Rate limiter for error capture to prevent flooding
const captureRateLimit = rateLimit({
  windowMs: 60000, // 1 minute
  maxAttempts: 50, // Max 50 errors per minute
});

/**
 * Main error capture function for FEMA system
 * @param {string} type - Error type/identifier
 * @param {Error|string|Object} error - Error object, message, or custom data
 * @param {FemaCaptureOptions} options - Additional options
 * @param {string} options.category - Error category (network, ui, engine, etc.)
 * @param {string} options.severity - Error severity (error, warning, info)
 * @param {Object} options.context - Additional context data
 * @param {boolean} options.skipSanitization - Skip PII sanitization (use with caution)
 * @param {Object} options.user - User information (will be sanitized)
 * @returns {string|null} Error ID if captured, null if rate limited
 */
export const femaCapture = (type: string, error: Error | string | unknown, options: FemaCaptureOptions = {}): string | null => {
  try {
    // Check rate limiting
    if (!captureRateLimit.check(`capture:${type}`)) {
      console.warn(`FEMA: Rate limit exceeded for error type: ${type}`);
      return null;
    }

    // Extract error information
    const errorInfo = extractErrorInfo(error);
    
    // Classify the error
    const classification = classifyError(type, errorInfo, options);
    
    // Prepare context data
    let context: Record<string, any> = {
      ...options.context,
      type,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      viewportSize: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
    };

    // Add user information if provided
    if (options.user) {
      context.user = options.skipSanitization ? options.user : sanitizePII(options.user);
    }

    // Sanitize the entire context unless explicitly skipped
    if (!options.skipSanitization) {
      context = sanitizePII(context);
    }

    // Create error entry
    const errorEntry = {
      message: errorInfo.message,
      stack: errorInfo.stack,
      category: classification.category,
      severity: classification.severity,
      context,
      fingerprint: generateErrorFingerprint(type, errorInfo, classification),
    };

    // Add to FEMA store
    const femaStore = useFemaStore.getState();
    const errorId = femaStore.addError(errorEntry as Partial<FemaError>);

    // Log to console in development
    if (process?.env?.NODE_ENV === 'development') {
      if (classification.severity === 'error') {
        console.error('FEMA Capture:', {
          id: errorId,
          type,
          category: classification.category,
          severity: classification.severity,
          message: errorInfo.message,
          context,
        });
      } else {
        console.warn('FEMA Capture:', {
          id: errorId,
          type,
          category: classification.category,
          severity: classification.severity,
          message: errorInfo.message,
          context,
        });
      }
    }

    return errorId;
  } catch (captureError) {
    // Prevent infinite loops - log capture errors directly to console
    console.error('FEMA: Error during capture:', captureError);
    return null;
  }
};

/**
 * Extract standardized error information from various error types
 * @param {Error|string|Object} error - Error to extract info from
 * @returns {ErrorInfo} Standardized error info
 */
const extractErrorInfo = (error: Error | string | unknown): ErrorInfo => {
  if (error instanceof Error) {
    return {
      message: error.message,
      stack: error.stack || null,
      name: error.name,
      cause: (error as any).cause,
    };
  }
  
  if (typeof error === 'string') {
    return {
      message: error,
      stack: null,
      name: 'StringError',
    };
  }
  
  if (error && typeof error === 'object') {
    const errorObj = error as any;
    return {
      message: errorObj.message || errorObj.msg || JSON.stringify(error),
      stack: errorObj.stack || null,
      name: errorObj.name || 'ObjectError',
      ...errorObj,
    };
  }
  
  return {
    message: 'Unknown error',
    stack: null,
    name: 'UnknownError',
  };
};

/**
 * Generate a fingerprint for error deduplication
 * @param {string} type - Error type
 * @param {ErrorInfo} errorInfo - Error information
 * @param {ErrorClassification} classification - Error classification
 * @returns {string} Error fingerprint
 */
const generateErrorFingerprint = (type: string, errorInfo: ErrorInfo, classification: ErrorClassification): string => {
  // Normalize stack trace for fingerprinting
  const normalizedStack = errorInfo.stack 
    ? errorInfo.stack
        .split('\n')
        .slice(0, 3) // First 3 lines
        .map(line => line.replace(/:\d+:\d+/g, ':X:X')) // Remove line numbers
        .join('\n')
    : '';

  const fingerprintData = {
    type,
    category: classification.category,
    message: errorInfo.message?.substring(0, 100), // First 100 chars
    stack: normalizedStack,
  };

  // Create hash
  const combined = JSON.stringify(fingerprintData);
  let hash = 0;
  
  for (let i = 0; i < combined.length; i++) {
    const char = combined.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  return Math.abs(hash).toString(36);
};

/**
 * Capture React component errors
 * @param {Error} error - React error
 * @param {ReactErrorInfo} errorInfo - React error info
 * @param {FemaCaptureOptions} options - Additional options
 */
export const captureReactError = (error: Error, errorInfo: ReactErrorInfo | undefined, options: FemaCaptureOptions = {}): string | null => {
  return femaCapture('react_error', error, {
    category: 'react',
    severity: 'error',
    context: {
      componentStack: errorInfo?.componentStack,
      ...options.context,
    },
    ...options,
  });
};

/**
 * Capture Promise rejections
 * @param {PromiseRejectionEvent} event - Unhandled rejection event
 */
export const captureUnhandledRejection = (event: PromiseRejectionEvent): string | null => {
  return femaCapture('unhandled_promise_rejection', event.reason, {
    category: 'promise',
    severity: 'error',
    context: {
      type: 'unhandledrejection',
      promise: event.promise?.toString(),
    },
  });
};

/**
 * Capture JavaScript errors
 * @param {ErrorEvent} event - Error event
 */
export const captureJavaScriptError = (event: ErrorEvent): string | null => {
  return femaCapture('javascript_error', event.error || event.message, {
    category: 'javascript',
    severity: 'error',
    context: {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      source: (event as any).source,
    },
  });
};

/**
 * Capture performance issues
 * @param {string} metric - Performance metric name
 * @param {number} value - Metric value
 * @param {FemaCaptureOptions} options - Additional options
 */
export const capturePerformance = (metric: string, value: number, options: FemaCaptureOptions = {}): string | null => {
  // Only capture if value exceeds threshold
  const thresholds: Record<string, number> = {
    fcp: 2000, // First Contentful Paint
    lcp: 2500, // Largest Contentful Paint
    fid: 100,  // First Input Delay
    cls: 0.1,  // Cumulative Layout Shift
    ttfb: 500, // Time to First Byte
  };

  const threshold = thresholds[metric] || options.threshold;
  if (threshold && value <= threshold) {
    return null;
  }

  return femaCapture(`performance_${metric}`, `${metric} exceeded threshold`, {
    category: 'performance',
    severity: value > (threshold * 2) ? 'error' : 'warning',
    context: {
      metric,
      value,
      threshold,
      ...options.context,
    },
    ...options,
  });
};

/**
 * Capture user interactions for debugging
 * @param {string} action - User action
 * @param {Record<string, any>} data - Action data
 * @param {FemaCaptureOptions} options - Additional options
 */
export const captureUserAction = (action: string, data: Record<string, any> = {}, options: FemaCaptureOptions = {}): string | null => {
  return femaCapture(`user_action_${action}`, `User performed: ${action}`, {
    category: 'user',
    severity: 'info',
    context: {
      action,
      data: sanitizePII(data),
      ...options.context,
    },
    skipSanitization: true, // Already sanitized data
    ...options,
  });
};

/**
 * Setup global error handlers
 */
export const setupGlobalErrorHandlers = (): void => {
  // Handle unhandled JavaScript errors
  window.addEventListener('error', captureJavaScriptError);
  
  // Handle unhandled Promise rejections
  window.addEventListener('unhandledrejection', captureUnhandledRejection);
  
  // Handle resource loading errors
  window.addEventListener('error', (event) => {
    if (event.target && event.target !== window) {
      femaCapture('resource_load_error', 'Failed to load resource', {
        category: 'resource',
        severity: 'warning',
        context: {
          tagName: (event.target as HTMLElement)?.tagName,
          src: (event.target as any)?.src || (event.target as any)?.href,
          type: (event.target as HTMLElement)?.tagName?.toLowerCase(),
        },
      });
    }
  }, true); // Use capture phase

  // Handle navigation errors
  window.addEventListener('beforeunload', () => {
    const femaStore = useFemaStore.getState();
    femaStore.processReportingQueue(); // Flush remaining errors
  });
};

/**
 * Remove global error handlers
 */
export const removeGlobalErrorHandlers = (): void => {
  window.removeEventListener('error', captureJavaScriptError);
  window.removeEventListener('unhandledrejection', captureUnhandledRejection);
};

export default femaCapture;
